import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2, AlertTriangle } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const PlanSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Nome muito curto")
		.max(50, "Nome muito longo")
		.required("Informe o nome"),
	value: Yup.number().min(0, "Valor inválido"),
});

// Estes campos são gravados no plano, mas hoje nada no sistema lê essas
// chavinhas: nenhuma tela é bloqueada por elas. A UI diz isso em vez de
// prometer um bloqueio que não acontece.
const RECURSOS = [
	{ campo: "useCampaigns", rotulo: "Disparo em massa (campanhas)" },
	{ campo: "useSchedules", rotulo: "Mensagem programada para depois" },
	{ campo: "useInternalChat", rotulo: "Conversa entre os atendentes" },
	{ campo: "useExternalApi", rotulo: "Ligar outro sistema neste" },
	{ campo: "useKanban", rotulo: "Quadro de cartões (Kanban)" },
];

const LIMITES = [
	{
		campo: "users",
		rotulo: "Pessoas usando o sistema",
		ajuda: "Quantos atendentes a empresa pode cadastrar.",
	},
	{
		campo: "connections",
		rotulo: "Números de WhatsApp",
		ajuda: "Quantos telefones ela pode conectar ao sistema.",
	},
	{
		campo: "queues",
		rotulo: "Filas de atendimento",
		ajuda: "Setores como Vendas, Suporte, Financeiro.",
	},
];

const PlanModal = ({ open, onClose, planId }) => {
	const initialState = {
		name: "",
		value: 0,
		users: 0,
		connections: 0,
		queues: 0,
		useCampaigns: true,
		useSchedules: true,
		useInternalChat: true,
		useExternalApi: true,
		useKanban: true,
		isActive: true,
	};

	const [plan, setPlan] = useState(initialState);

	useEffect(() => {
		(async () => {
			if (!planId) return;
			try {
				const { data } = await api.get(`/plans/${planId}`);
				setPlan((anterior) => ({ ...anterior, ...data }));
			} catch (err) {
				toastError(err);
			}
		})();

		return () => setPlan(initialState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [planId, open]);

	const handleClose = () => {
		onClose();
		setPlan(initialState);
	};

	const handleSave = async (values) => {
		try {
			if (planId) {
				await api.put(`/plans/${planId}`, values);
			} else {
				await api.post("/plans", values);
			}
			toast.success("Plano salvo.");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && handleClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{planId ? "Alterar este plano" : "Criar um plano para vender"}
					</DialogTitle>
					<DialogDescription>
						{planId
							? "O que você mudar aqui passa a valer na hora para todas as empresas que já usam este plano."
							: "Defina o preço, até onde a empresa pode ir e o que ela pode usar. Dá para mudar tudo depois."}
					</DialogDescription>
				</DialogHeader>
				<Formik
					initialValues={plan}
					enableReinitialize
					validationSchema={PlanSchema}
					onSubmit={(values, actions) => {
						handleSave(values);
						actions.setSubmitting(false);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form className="space-y-4">
							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label htmlFor="name">Nome do plano</Label>
									<Field
										as={Input}
										id="name"
										name="name"
										autoFocus
										placeholder="Ex.: Básico"
									/>
									{touched.name && errors.name && (
										<p className="text-xs text-destructive">{errors.name}</p>
									)}
									<p className="text-xs leading-relaxed text-muted-foreground">
										É o nome que você vê ao escolher o plano de uma empresa.
									</p>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="value">Quanto a empresa paga por mês</Label>
									<Field
										as={Input}
										id="value"
										name="value"
										type="number"
										step="0.01"
										min="0"
										placeholder="0,00"
									/>
									<p className="text-xs leading-relaxed text-muted-foreground">
										É o valor que entra na fatura mensal dessa empresa.
									</p>
								</div>
							</div>

							<div>
								<p className="text-sm font-medium">Até onde a empresa pode ir</p>
								<p className="mb-2 mt-0.5 text-xs leading-relaxed text-muted-foreground">
									Quando bate no limite, o sistema deixa de aceitar novos
									cadastros e mostra um aviso para a empresa.{" "}
									<strong>0 = sem limite nenhum.</strong>
								</p>
								<div className="grid gap-3 sm:grid-cols-3">
									{LIMITES.map((item) => (
										<div key={item.campo} className="space-y-1.5">
											<Label htmlFor={item.campo}>{item.rotulo}</Label>
											<Field
												as={Input}
												id={item.campo}
												name={item.campo}
												type="number"
												min="0"
											/>
											<p className="text-xs leading-relaxed text-muted-foreground">
												{item.ajuda}
												{Number(values[item.campo]) > 0
													? ""
													: " Hoje: sem limite."}
											</p>
										</div>
									))}
								</div>
							</div>

							<div>
								<p className="text-sm font-medium">
									O que este plano promete (só anotação)
								</p>
								<div className="mb-3 mt-1.5 flex gap-2 rounded-md bg-muted p-2.5 text-xs leading-relaxed">
									<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
									<span>
										Estas chavinhas ficam guardadas no plano, mas hoje elas{" "}
										<strong>não bloqueiam nada</strong>: mesmo desligadas, a
										empresa continua enxergando e usando o recurso. Servem só
										para você anotar o que combinou na venda. Quem limita de
										verdade são os números do bloco acima.
									</span>
								</div>
								<div className="space-y-1">
									{RECURSOS.map((recurso) => (
										<div
											key={recurso.campo}
											className="flex items-center justify-between gap-3 rounded-md px-1 py-1.5"
										>
											<Label
												htmlFor={recurso.campo}
												className="text-sm font-normal"
											>
												{recurso.rotulo}
											</Label>
											<Switch
												id={recurso.campo}
												checked={values[recurso.campo]}
												onCheckedChange={(marcado) =>
													setFieldValue(recurso.campo, marcado)
												}
											/>
										</div>
									))}
								</div>
							</div>

							<div className="space-y-2 rounded-lg border p-3">
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<Label htmlFor="isActive" className="text-sm font-medium">
											Plano disponível para venda
										</Label>
										<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
											Desligado, ele some da lista na hora de cadastrar uma
											empresa nova.
										</p>
									</div>
									<Switch
										id="isActive"
										checked={values.isActive}
										onCheckedChange={(marcado) =>
											setFieldValue("isActive", marcado)
										}
									/>
								</div>
								{!values.isActive && (
									<div className="flex gap-2 rounded-md bg-muted p-2.5 text-xs leading-relaxed">
										<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
										<span>
											Quem já está neste plano continua usando normalmente e
											continua sendo cobrado. Isto só fecha a porta para
											contratações novas.
										</span>
									</div>
								)}
							</div>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={handleClose}
									disabled={isSubmitting}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{planId ? "Salvar alterações" : "Criar plano"}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default PlanModal;
